import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MuiDialog from '../../SystemDesign/MuiDialog';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { DELETE_REQUIREMENT } from '../../Reducers/Actions';
import { useDeleteSkillPosteRequirementMutation } from '../../services/SkillsManagement';

/**
 * DeleteRequirementsSkill - delete a requirement (skill assigned to poste)
 * Props:
 *  - row: requirement row (expects `id` or `skill_poste_id`, and optional attached `skill` and `poste`)
 */
const DeleteRequirementsSkill = ({ row }) => {
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [deleteRequirement, { isLoading }] = useDeleteSkillPosteRequirementMutation();

  const req = row?.original || row || {};
  const requirementId = req?.id ?? req?.skill_poste_id;
  if (!requirementId) return null;

  const skillName = req?.skill?.name || `Requirement #${requirementId}`;

  const handleDelete = async () => {
    try {
      try {
        // Call API to delete requirement
        const response = await deleteRequirement({ skillPosteId: requirementId }).unwrap();

        // Update Redux store immediately
        dispatch({ type: DELETE_REQUIREMENT, payload: { id: requirementId } });

        // Build success message
        let successMessage;
        if (typeof response === 'string') successMessage = response;
        else if (response && typeof response === 'object' && response.message) successMessage = response.message;
        else successMessage = `Requirement for "${skillName}" deleted successfully`;

        setInfoAlert({ severity: 'success', message: successMessage });
        setOpenSnackBar(true);
        return true;
      } catch (parseError) {
        // Some servers return plain text with 200
        if (parseError?.originalStatus === 200) {
          dispatch({ type: DELETE_REQUIREMENT, payload: { id: requirementId } });
          const successMessage = parseError.data || `Requirement for "${skillName}" deleted successfully`;
          setInfoAlert({ severity: 'success', message: successMessage });
          setOpenSnackBar(true);
          return true;
        }
        throw parseError;
      }
    } catch (error) {
      console.error('Error deleting requirement:', error);
      let errorMessage = 'An error occurred while deleting the requirement';
      if (error?.data?.message) errorMessage = typeof error.data.message === 'string' ? error.data.message : JSON.stringify(error.data.message);
      else if (error?.message) errorMessage = error.message;
      setInfoAlert({ severity: 'error', message: errorMessage });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <DeleteIcon fontSize="small" color="error" />
      </ListItemIcon>
      <ListItemText>Delete</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <div>
      <p>Are you sure you want to delete the requirement for "{skillName}"?</p>
      <p>This action is irreversible.</p>
    </div>
  );

  return (
    <MuiDialog
      icon={<DeleteOutlineIcon />}
      triggerButton={triggerButton}
      title="Delete Requirement"
      subtitle={`You are about to delete the requirement for ${skillName}`}
      content={dialogContent}
      maxWidth="xs"
      existAction={true}
      actionTitle="Delete"
      actionFunction={handleDelete}
      isLoading={isLoading}
    />
  );
};

// Static props for menu use
DeleteRequirementsSkill.menuIcon = <DeleteOutlineIcon fontSize="small" />;
DeleteRequirementsSkill.menuLabel = 'Delete';

export default DeleteRequirementsSkill;

